/**
 * UTM Source Statistics Component
 * Displays traffic source breakdown including ChatGPT referrals
 */

import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

type UtmSourceRow = {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  visitor_count: number;
  event_count: number;
  business_visitors: number;
  first_seen: number;
  last_seen: number;
};

type UtmSourceStatsProps = {
  siteId: string;
};

export function UtmSourceStats({ siteId }: UtmSourceStatsProps) {
  const [sources, setSources] = useState<UtmSourceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!siteId) return;

    const fetchUtmStats = async () => {
      try {
        setLoading(true);
        const res = await fetch(`/api/utm-stats?site_id=${encodeURIComponent(siteId)}`);

        if (!res.ok) {
          throw new Error(`Failed to fetch UTM stats: ${res.statusText}`);
        }

        const data = await res.json();
        setSources(data.sources || []);
        setError(null);
      } catch (err) {
        console.error("Failed to load UTM stats:", err);
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    };

    fetchUtmStats();

    // Refresh every 30 seconds
    const interval = setInterval(fetchUtmStats, 30000);
    return () => clearInterval(interval);
  }, [siteId]);

  if (loading && sources.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>🏷️ Traffic Sources (UTM)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>🏷️ Traffic Sources (UTM)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-destructive">Error: {error}</div>
        </CardContent>
      </Card>
    );
  }

  if (sources.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>🏷️ Traffic Sources (UTM)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              No UTM-tagged visits yet. Add UTM parameters to your links:
            </div>
            <div className="bg-muted p-3 rounded-md text-xs font-mono">
              ?utm_source=chatgpt.com&utm_medium=ai_chat&utm_campaign=test
            </div>
            <div className="text-xs text-muted-foreground">
              💡 Use <code className="bg-muted px-1 py-0.5 rounded">utm-test.html</code> or{" "}
              <code className="bg-muted px-1 py-0.5 rounded">scripts/generate-tracking-url.ts</code>{" "}
              to create tracking URLs
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Highlight ChatGPT sources
  const chatgptSources = sources.filter(
    (s) => s.utm_source?.includes("chatgpt") || s.utm_source?.includes("openai")
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          🏷️ Traffic Sources (UTM)
          {chatgptSources.length > 0 && (
            <span className="text-xs font-normal bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
              🤖 {chatgptSources.length} ChatGPT
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {sources.map((source, idx) => {
            const isChatGPT =
              source.utm_source?.includes("chatgpt") ||
              source.utm_source?.includes("openai");

            const sourceLabel = source.utm_source || "(direct)";
            const mediumLabel = source.utm_medium ? ` · ${source.utm_medium}` : "";
            const campaignLabel = source.utm_campaign ? ` · ${source.utm_campaign}` : "";

            return (
              <div
                key={idx}
                className={`p-3 rounded-lg border ${
                  isChatGPT ? "bg-blue-50 border-blue-200" : "bg-muted/50"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-1 flex-1">
                    <div className="flex items-center gap-2">
                      {isChatGPT && <span className="text-sm">🤖</span>}
                      <div className="font-medium text-sm">
                        {sourceLabel}
                        <span className="text-muted-foreground font-normal">
                          {mediumLabel}
                          {campaignLabel}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-4 text-xs text-muted-foreground">
                      <span>{source.visitor_count} visitors</span>
                      <span>{source.event_count} events</span>
                      {source.business_visitors > 0 && (
                        <span className="text-green-600 font-medium">
                          {source.business_visitors} business
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(source.last_seen).toLocaleDateString()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {chatgptSources.length > 0 && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="text-sm font-medium text-green-800 flex items-center gap-2">
              ✅ ChatGPT Traffic Detected
            </div>
            <div className="text-xs text-green-700 mt-1">
              {chatgptSources.reduce((sum, s) => sum + s.visitor_count, 0)} visitors from ChatGPT conversations
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
